import { Injectable } from '@angular/core';
import { Action, select, Store } from '@ngrx/store';
import { createEffect, Actions, ofType } from '@ngrx/effects';

import * as actions from '../actions';
import * as selectors from '../selectors';

import * as Services from '@shared/core/services';
import * as Utils from '@shared/core/utils';

import { Observable, of, never, combineLatest } from 'rxjs';
import { catchError, switchMap, map, filter, take, auditTime, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class OnlineMenuEffects {
    public onMenuUpdateNeeded$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.CurrentLocationPickupTimeSet, actions.CurrentLocationSet, actions.SetCollectionType, actions.PatchOrderTypeIdCollectionTypeValue),
            auditTime(100),
            switchMap(() => this._getDataForOnlineMenuPagesRequest$()),
            switchMap(([currentOrderTypeId, isOnLocationDetailsPage, isCurrentRouteCheckoutPage, currentLocation, pickupTime, onlineMenu, cartOrderTypeId, cartLocationNo]) => {
                const shouldRequestOnlineMenu = this._shouldRequestOnlineMenu({
                    currentOrderTypeId,
                    isOnLocationDetailsPage,
                    isCurrentRouteCheckoutPage,
                    currentLocation,
                    pickupTime,
                    onlineMenu,
                    cartOrderTypeId,
                    cartLocationNo,
                });

                if (!shouldRequestOnlineMenu) return never();

                const locationNo = isOnLocationDetailsPage ? currentLocation.LocationNo : cartLocationNo;
                const orderTypeId = isOnLocationDetailsPage ? currentOrderTypeId : cartOrderTypeId;

                return of(actions.OnlineMenuPagesRequest(locationNo, orderTypeId, Utils.Dates.getLocalISOFormatDate(pickupTime.Date, true)));
            }),
        ),
    );

    private _shouldRequestOnlineMenu(params: {
        currentOrderTypeId: number;
        isOnLocationDetailsPage: boolean;
        isCurrentRouteCheckoutPage: boolean;
        currentLocation: OLO.DTO.OnlineOrderingLocationBusinessModel;
        pickupTime: OLO.Ordering.PickupTime;
        onlineMenu: OLO.State.OnlineMenu;
        cartOrderTypeId: number;
        cartLocationNo: number;
    }): boolean {
        const { currentOrderTypeId, isOnLocationDetailsPage, isCurrentRouteCheckoutPage, currentLocation, pickupTime, onlineMenu, cartOrderTypeId, cartLocationNo } = params;

        // Check to request online meunu only for location details or checkout page
        if (!isOnLocationDetailsPage && !isCurrentRouteCheckoutPage) {
            return false;
        }

        const locationNo = isOnLocationDetailsPage ? currentLocation.LocationNo : cartLocationNo;
        const orderTypeId = isOnLocationDetailsPage ? currentOrderTypeId : cartOrderTypeId;

        // Check that online menu has already downloaded for proper locationNo and orderTypeId
        const hasValidMenu = onlineMenu.data !== null && onlineMenu.locationNo === locationNo && onlineMenu.orderTypeId === orderTypeId;

        // Check for location details page that current location has current order type id supported
        if (isOnLocationDetailsPage) {
            const hasValidOrderType = currentLocation?.OrderTypes.some((orderType) => orderType.Id === currentOrderTypeId);
            if (!hasValidOrderType || !hasValidMenu) {
                return true;
            }
        }

        const isValidTimeRange = Utils.Dates.isHourInHoursRange(pickupTime.Hour, onlineMenu.data?.StartTime, onlineMenu.data?.EndTime);
        const isSameDay = pickupTime.DateLocalISO.split('T')[0] === onlineMenu.pickupDate?.split('T')[0];

        if (!isValidTimeRange || !isSameDay) {
            return true;
        }

        return false;
    }

    public requestOnlineMenu$: Observable<Action> = createEffect(() =>
        this._actions$.pipe(
            ofType(actions.OnlineMenuPagesRequest),
            switchMap(({ locationNo, orderTypeId, pickupDate }) =>
                this._onlineMenuService
                    .getMenuPages({
                        locationNo,
                        orderTypeId,
                        menuDate: pickupDate,
                    })
                    .pipe(
                        withLatestFrom(this._store.select(selectors.getLocationDetails(locationNo))),
                        map(([response, locationDetails]) => {
                            const onlineMenuHasFeaturedProductPage = response.Pages.some((page) => page.PageType === OLO.Enums.ONLINE_MENU_PAGE_TYPE.FEATURED_PRODUCT_PAGE);
                            const hasOnlineLocationClassification = locationDetails?.VirtualLocations?.some(
                                (virtualLocation) => virtualLocation?.LocationClassification === OLO.Enums.LOCATION_CLASSIFICATION.ONLINE_LOCATION,
                            );

                            if (!onlineMenuHasFeaturedProductPage && hasOnlineLocationClassification) {
                                console.warn(
                                    `WARNING! The physcial location no ${locationDetails.LocationNo} has virtual locations with ONLINE LOCATION classification.` +
                                        'The physical location can not have online menu assigned in that case, the physical location should have only featured products page.',
                                );
                            }

                            /* Sort first by DisplayIndex */
                            response.Pages = response.Pages.sort((a, b) => {
                                switch (true) {
                                    case a.DisplayIndex < b.DisplayIndex:
                                        return -1;
                                    case a.DisplayIndex > b.DisplayIndex:
                                        return 1;
                                    default:
                                        return 0;
                                }
                            });

                            return actions.OnlineMenuPagesSuccessRequest(locationNo, orderTypeId, pickupDate, response);
                        }),
                        catchError((ex) => {
                            console.error(ex);

                            return [actions.OnlineMenuPageErrorRequest(locationNo, orderTypeId, pickupDate, ex)];
                        }),
                    ),
            ),
        ),
    );

    private _getDataForOnlineMenuPagesRequest$() {
        return combineLatest(
            this._store.pipe(
                select(selectors.getOrderTypeId),
                filter((orderTypeId) => !!orderTypeId),
            ),
            this._store.pipe(select(selectors.routeIsLocationDetailsPage())),
            this._store.pipe(select(selectors.isCurrentRouteCheckoutPage)),
            this._store.pipe(select(selectors.getCurrentLocationDetails)),
            this._store.pipe(
                select(selectors.getCurrentPickupTime),
                filter((pickupTime) => !!pickupTime),
            ),
            this._store.pipe(
                select(selectors.getOnlineMenu),
                filter((onlineMenu) => !onlineMenu.isDownloading),
            ),
            this._store.pipe(select(selectors.getCartOrderTypeId)),
            this._store.pipe(select(selectors.getCartLocationNo)),
        ).pipe(take(1));
    }

    constructor(private _actions$: Actions, private _onlineMenuService: Services.OnlineMenuService, private _store: Store<OLO.State>) {}
}
